/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/require-computed-property-dependencies, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import InputType from '@linc-technologies/linced-common/enums/input-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import FieldAccessRoles from '@linc-technologies/linced-common/enums/roles/field-access';
import { observes } from '@ember-decorators/object';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class GroupController extends Controller {
  // Services
  @service
  paperToaster;

  @service
  session;

  @service
  store;

  InputType = InputType;
  FieldAccessRoles = FieldAccessRoles;
  DataType = DataType;
  PersonType = PersonType;

  // inputTypes is a limited list - there are some types used for School Options which we don't want here.
  inputTypes = Object.freeze([
    { value: 1, label: 'Text' },
    { value: 2, label: 'Textarea' },
    { value: 3, label: 'Radio boxes' },
    { value: 4, label: 'Checkbox(es)' },
    { value: 5, label: 'Date' },
    { value: 6, label: 'Email address' },
    { value: 7, label: 'Number' },
    { value: 8, label: 'URL' },
    { value: 9, label: 'Select' },
    { value: 10, label: 'Multiple select (chips)' },
    { value: 11, label: 'Switch' },
    { value: 14, label: 'Jurisdictional ID' },
  ]);

  numericTypes = Object.freeze([
    { value: 3, label: 'Integer (whole numbers)' },
    { value: 4, label: 'Float (decimal numbers)' },
  ]);
  selectDataTypes = Object.freeze([
    { value: 1, label: 'String' },
    { value: 3, label: 'Integer (whole numbers)' },
  ]);

  /*{{!-- enum InputType {
    InputType_Unspecified = 0;
    InputType_Text = 1;
      InputType_Date = 5;
      InputType_Email = 6;
      InputType_Number = 7;
      InputType_Url = 8;
    InputType_Textfield = 2;
    InputType_Radio = 3;
    InputType_Checkbox = 4;
    InputType_Select = 9;
}

// DataType is based on basic Go types
enum DataType {
    DataType_Unspecified = 0;
    DataType_String = 1;
    // List is provided for things like tags, where they can be dynamically added, reordered and deleted.
    DataType_List = 2;
    DataType_Int = 3;
    DataType_Float = 4;
    DataType_Bool = 5;
} --}}*/

  @tracked
  fieldId = '';

  @tracked
  fieldLabel = '';

  @tracked
  numericType = null;

  @action
  setNumericType(value) {
    this.numericType = value;
  }

  @tracked
  selectDataType = null;

  @action
  setSelectDataType(value) {
    this.selectDataType = value;
  }

  @observes('fieldType.value', 'selectDataType')
  _updateFieldType() {
    let type = this.get('fieldType.value');
    if (!isEmpty(type) && !isEmpty(this.field)) {
      this.field.set('inputType', type);
      if (type === InputType.JURISDICTIONAL_ID) {
        this.field.set('studentPermission', false);
      }
      if (type !== InputType.NUMBER) {
        let dataType = 0;
        switch (type) {
          case InputType.CHECKBOX:
          case InputType.MULTI_SELECT:
            dataType = DataType.LIST;
            break;
          case InputType.SWITCH:
            dataType = DataType.BOOLEAN;
            break;
          case InputType.SELECT:
            dataType = this.selectDataType || DataType.STRING;
            break;
          default:
            dataType = DataType.STRING;
            break;
        }
        this.field.set('dataType', dataType);
      }
    }
    if (type === InputType.NUMBER) {
      if (!isEmpty(this.field)) {
        switch (this.field.dataType) {
          case DataType.INTEGER:
          case DataType.FLOAT:
            this.numericType = this.getNumericType(this.field.dataType);
            break;
          default:
            this.numericType = this.getNumericType(DataType.INTEGER);
            break;
        }
      } else {
        this.numericType = this.getNumericType(DataType.INTEGER);
      }
    }
  }

  @observes('fieldType.value', 'numericType.value')
  _updateNumericType() {
    if (this.get('fieldType.value') === InputType.NUMBER) {
      let type = this.get('numericType.value');
      if (!isEmpty(type) && !isEmpty(this.field)) {
        this.field.set('dataType', type);
      }
    }
  }

  @computed('fieldType')
  get fieldTypeHasOptions() {
    let fieldType = this.get('fieldType.value');
    switch (fieldType) {
      case 3:
      case 4:
      case 9:
      case 10:
        return true;
    }

    return false;
  }

  newOption = null;

  @tracked
  fieldDialogOpen = false;

  @tracked
  saveInProgress = false;

  @tracked
  deleteFieldDialogOpen = false;

  @tracked
  deleteInProgress = false;

  @tracked
  errorMessage = '';

  isSortable = false;

  @tracked
  field;

  @tracked
  newOptionLabel;

  @tracked
  newOptionValue;

  @action
  setNewOptionValue(value) {
    this.newOptionValue = value;
  }

  @tracked
  fieldType;

  @action
  setFieldType(value) {
    this.fieldType = value;
  }

  init() {
    super.init(...arguments);
    this.resetForm();
  }

  @computed('model.group.personType')
  get filteredRoleOptions() {
    switch (this.model.group.personType) {
      case PersonType.LEARNER:
        return [
          FieldAccessRoles.STUDENT_MEDICAL,
          FieldAccessRoles.STUDENT_SENSITIVE,
        ];

      case PersonType.STAFF:
        return [
          FieldAccessRoles.STAFF_MEDICAL,
          FieldAccessRoles.STAFF_SENSITIVE,
        ];

      case PersonType.CAREGIVER:
        return [
          FieldAccessRoles.CAREGIVER_MEDICAL,
          FieldAccessRoles.CAREGIVER_SENSITIVE,
        ];
    }
    return [];
  }

  // toggleDialog opens the field dialog.
  toggleDialog() {
    this.errorMessage = '';
    this.toggleProperty('fieldDialogOpen');
    if (!this.fieldDialogOpen) {
      this.field.rollbackAttributes();
      this.resetForm();
    }
  }

  @action
  toggleFieldDialog() {
    this.toggleDialog();
  }

  @action
  editField(id) {
    this.store.findRecord('people-schema/field', id).then((field) => {
      let props = {
        field,
      };
      let inputType = field.get('inputType');
      props.fieldType = this.getInputType(inputType);
      if (inputType === InputType.NUMBER) {
        props.numericType = this.getNumericType(field.get('dataType'));
      }
      if (inputType === InputType.SELECT) {
        props.selectDataType = findBy(
          this.selectDataTypes,
          'value',
          field.get('dataType'),
        );
      }
      this.setProperties(props);
    });
    this.fieldId = id;
    this.toggleDialog();
  }

  @action
  saveField() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let action = 'create';

    if (isEmpty(this.fieldId)) {
      this.field.set('templateGroupId', this.get('model.group'));
      this.field.set('templateSetId', this.get('model.templateSet'));
      this.field.set('sequence', this.get('model.fields.length') + 1);
    } else {
      action = 'update';
    }

    switch (this.get('fieldType.value')) {
      case InputType.NUMBER:
        this.field.set('dataType', this.get('numericType.value'));
        break;
      case InputType.SELECT:
        this.field.set('dataType', this.selectDataType.value);
        break;
    }

    return this.field
      .save()
      .then(() => {
        this.fieldDialogOpen = false;
        this.resetForm();
      })
      .catch((err) => {
        this.handleError(err, action);
        this.field.rollbackAttributes();
      })
      .then(() => {
        this.saveInProgress = false;
        this.get('model.fields').update();
      });
  }

  @action
  deleteField(id) {
    this.errorMessage = '';
    this.store.findRecord('people-schema/field', id).then((field) => {
      this.fieldLabel = field.get('label');
    });
    this.fieldId = id;
    this.deleteFieldDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let field = this.store.peekRecord('people-schema/field', this.fieldId);

        // Delete record (locally + pushed to server).
        return field
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            field.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteFieldDialogOpen = false;
        this.resetForm();
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  @action
  addOption() {
    if (isEmpty(this.get('field.options'))) {
      this.field.set('options', []);
    }
    if (this.selectDataType.value === DataType.INTEGER) {
      this.field.options.pushObject({
        valueInt: parseInt(this.newOptionValue, 10),
        label: this.newOptionLabel,
      });
    } else {
      this.field.options.pushObject({
        value: this.newOptionValue,
        label: this.newOptionLabel,
      });
    }
    this.newOptionValue = '';
    this.newOptionLabel = '';
  }

  @action
  removeOption(option) {
    this.field.options.removeObject(option);
  }

  @action
  maybeMutBoth(a, b, context, newValue) {
    if (context.get(a) === context.get(b)) {
      context.set(b, newValue);
    }
    context.set(a, newValue);
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    targetList.forEach((entity, index) => {
      entity.set('sequence', index + 1);
      entity.save();
    });
  }

  @action
  optionSortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    // options is a @fragmentArray; .splice() comes from the
    // fragment-array-mutable initializer (StatefulArray only has
    // .replace() natively).
    const [item] = sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);
  }

  getInputType(inputType) {
    let types = this.inputTypes;
    return findBy(types, 'value', inputType);
  }

  getNumericType(dataType) {
    let types = this.numericTypes;
    return findBy(types, 'value', dataType);
  }

  handleError(err) {
    if (!isEmpty(err.errors.msg)) {
      this.errorMessage = err.errors.msg;
    } else {
      this.errorMessage = err.message;
    }
  }

  setErrorMessage(code, action) {
    var message = 'An unknown error occurred.  Please try again.';
    const codes = {
      400: 'Sorry, your request was invalid.  Please check all details below, and try again.',
      403: {
        create:
          'Sorry, you are not authorised to create a new user.  Please check that you are logged in as the correct user, and try again.',
        update:
          'Sorry, you are not authorised to edit this user.  Please check that you are logged in as the correct user, and try again.',
        delete:
          'Sorry, you are not authorised to delete this user.  Please check that you are logged in as the correct user, and try again.',
      },
      409: 'A user already exists with that username.  Please double-check the details, or find and update the existing user.',
    };

    if (!isEmpty(codes[code][action])) {
      message = codes[code][action];
    } else if (!isEmpty(codes[code])) {
      message = codes[code];
    }

    this.errorMessage = message;
  }

  resetForm() {
    let field = this.field;
    if (field && field.get('currentState.isDirty')) {
      field.rollbackAttributes();
    }
    this.field = this.store.createRecord('people-schema/field', {});
    this.fieldId = '';
    this.fieldType = this.inputTypes[0];
    this.numericType = this.numericTypes[0];
    this.set('selectDataType', this.selectDataTypes[0]);
    this.newOptionLabel = '';
    this.newOptionValue = '';
  }
}
